import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collectionsData, collectionCategories } from "@/mocks/collections";

const categoryParamMap: Record<string, string> = {
  living: "Living",
  bedroom: "Bedroom",
  dining: "Dining",
  "custom-orders": "Custom Orders",
  "signature-pieces": "Signature Pieces",
};

export default function CollectionsGrid() {
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const catParam = searchParams.get("category");
    if (catParam && categoryParamMap[catParam]) {
      setActiveCategory(categoryParamMap[catParam]);
    }
  }, [searchParams]);

  const filtered =
    activeCategory === "All"
      ? collectionsData
      : collectionsData.filter((c) => c.category === activeCategory);

  return (
    <section className="bg-brand-cream py-16 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {collectionCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap font-display text-xs tracking-widest uppercase px-6 py-2.5 rounded-full border transition-all duration-300 cursor-pointer ${
                activeCategory === cat
                  ? "bg-brand-brown border-brand-brown text-brand-cream"
                  : "border-brand-brown/30 text-brand-brown hover:border-brand-gold hover:text-brand-gold"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((col) => (
            <Link
              key={col.id}
              to={`/product/${col.slug}`}
              className="group bg-brand-beige border border-brand-gold/10 rounded-lg overflow-hidden hover:border-brand-gold/35 transition-all duration-400 cursor-pointer block"
              data-product-shop
            >
              {/* Image */}
              <div className="w-full h-64 overflow-hidden relative">
                <img
                  src={col.image}
                  alt={col.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-brand-darkbrown/0 group-hover:bg-brand-darkbrown/20 transition-all duration-400" />
                <div className="absolute top-4 left-4 bg-brand-gold text-brand-darkbrown font-display text-[10px] tracking-widest uppercase px-3 py-1 rounded-full">
                  {col.category}
                </div>
                <div className="absolute top-4 right-4 bg-brand-darkbrown/50 backdrop-blur-sm text-brand-cream font-display text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border border-brand-gold/30">
                  Handcrafted
                </div>
              </div>

              {/* Content */}
              <div className="p-7">
                <h3 className="font-serif text-xl text-brand-darkbrown font-bold mb-1">{col.name}</h3>
                <p className="font-display text-sm text-brand-gold font-semibold mb-3">Visit Our Showroom</p>
                <p className="font-sans text-sm text-brand-muted leading-relaxed mb-4">{col.description}</p>
                <span className="whitespace-nowrap inline-flex items-center justify-center gap-2 font-display text-xs tracking-widest uppercase px-6 py-2.5 bg-transparent border-[1.5px] border-brand-gold text-brand-gold rounded-full hover:bg-brand-gold hover:text-brand-darkbrown transition-all duration-300 cursor-pointer w-full">
                  View Details
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Custom Design CTA */}
        <div className="mt-16 bg-brand-darkbrown rounded-lg p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <img
              src="https://readdy.ai/api/search-image?query=wood%20grain%20texture%20dark%20mahogany%20close%20up%20luxury%20background%20pattern&width=1200&height=400&seq=cta1&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10">
            <span className="font-display text-brand-gold text-xs tracking-[4px] uppercase block mb-4">Bespoke Service</span>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-cream font-bold mb-4">
              Can&apos;t Find Your Perfect Piece?
            </h2>
            <p className="font-sans text-brand-cream/60 text-sm max-w-lg mx-auto leading-relaxed mb-8">
              Our master craftsmen can create any furniture piece to your exact specifications. Custom dimensions,
              wood choices, upholstery — the possibilities are limitless.
            </p>
            <a
              href="#contact"
              className="whitespace-nowrap inline-block font-display text-sm tracking-widest uppercase px-12 py-4 bg-transparent border-[1.5px] border-brand-gold text-brand-gold rounded-full hover:bg-brand-gold hover:text-brand-darkbrown transition-all duration-300 cursor-pointer"
            >
              Request Custom Design
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
